import React from "react";
import styled from "styled-components";

import {
  Text,
  breakpoints,
  Button as AsurionButton,
} from "@trailhead/asurion-ui";
import { FlexColumnContainer, FlexRowContainer } from "@trailhead/core-ui";
import { AEClient, TwilioClient } from "@trailhead/chat";
import { trackEvent } from "@trailhead/analytics";

import ExpertChatImage from "../assets/ExpertChatImage.png";
import { triggerExpertMessagingOverlay } from "./TriggerExpertOverlay";
import { defaultChatPrompts } from "./models";
import { useSanitizedTranslation } from "@trailhead/i18n";

const ExpertChatContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: start;

  ${breakpoints.md} {
    width: 50%;
  }

  & > * {
    margin-top: -3rem !important;
  }
`;

const ExpertSetupContainer = styled(FlexColumnContainer)`
  justify-content: space-evenly;
  align-items: center;
  align-self: flex-start;
  flex-direction: column;
  width: 100%;
  padding: 2rem 1rem;
  gap: 20px;
  color: #000000;
  background-color: #fff;
  border: 1px solid #d5d6da;
  border-radius: 5px;
  ${breakpoints.sm} {
    padding: 3rem 1rem;
  }
  ${breakpoints.md} {
    border: none;
    box-shadow: 0px 4px 30px rgba(0, 0, 0, 0.2);
    padding: 3rem 2rem;
    width: 344px;
  }
  ${breakpoints.lg} {
    padding: 4rem 2rem;
    width: 375px;
  }
`;

const HeaderTitle = styled(Text)`
  text-align: center;
`;

const ButtonContainer = styled(FlexRowContainer)`
  justify-content: center;
  flex-wrap: wrap;
`;

const TextWrapper = styled.p`
  text-align: center;
  max-width: 375px;
`;

const ChatButtonsTitle = styled(HeaderTitle)`
  margin: 0;
  display: inline-block;
`;

const NoWrapText = styled.span`
  white-space: nowrap;
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  height: auto;
  max-height: 3rem;
  margin: 1% 1%;
  ${breakpoints.sm} {
    max-width: 350px;
  }
`;

const ExpertChatImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExpertChatImageSrc = styled.img`
  max-width: 220px;
  max-height: 90px;
`;

const TagButton = styled(AsurionButtonWrapper)`
  border-color: #a5aaaf;
  padding: 12px 10px;
  &:hover {
    border-color: #868889 !important;
  }
`;

interface ChatButtonProps {
  buttonText: string;
  chatText?: string;
  shouldAutoSendChatText?: boolean;
  chatClient?: typeof TwilioClient | typeof AEClient;
}

const ChatButton: React.FC<ChatButtonProps> = ({
  buttonText,
  chatText,
  shouldAutoSendChatText,
  chatClient = TwilioClient,
}) => {
  const t = useSanitizedTranslation();
  return (
    <TagButton
      size="medium"
      variant="outline"
      onClick={() => {
        triggerExpertMessagingOverlay(
          chatClient,
          chatText ? t(chatText) : chatText,
          shouldAutoSendChatText,
        );
        trackEvent("Chat_Prompt_Click", { buttonText });
      }}
    >
      {t(buttonText)}
    </TagButton>
  );
};

export const ExpertChat: React.FC<{
  chatClient?: typeof TwilioClient | typeof AEClient;
}> = ({ chatClient = TwilioClient }) => {
  const t = useSanitizedTranslation();
  const shouldAutoSend = true;
  const chatPrompts = defaultChatPrompts;

  return (
    <ExpertChatContainer>
      <ExpertSetupContainer>
        <ExpertChatImageContainer>
          <ExpertChatImageSrc src={ExpertChatImage} />
        </ExpertChatImageContainer>
        <TextWrapper>
          <ChatButtonsTitle size={3}>
            {t("Hi there ")}👋{" "}
            <NoWrapText>{t("We're your experts.")}</NoWrapText>
          </ChatButtonsTitle>
          <ChatButtonsTitle size={3} weight={"heavy"}>
            {t("Select a topic to start chat.")}
          </ChatButtonsTitle>
        </TextWrapper>
        <FlexColumnContainer>
          <ButtonContainer>
            <ChatButton
              chatClient={chatClient}
              buttonText={chatPrompts[0].buttonText}
              chatText={chatPrompts[0].chatText}
              shouldAutoSendChatText={shouldAutoSend}
            />
            <ChatButton
              chatClient={chatClient}
              buttonText={chatPrompts[1].buttonText}
              chatText={chatPrompts[1].chatText}
              shouldAutoSendChatText={shouldAutoSend}
            />
          </ButtonContainer>
          <ButtonContainer>
            <ChatButton
              chatClient={chatClient}
              buttonText={chatPrompts[2].buttonText}
              chatText={chatPrompts[2].chatText}
              shouldAutoSendChatText={shouldAutoSend}
            />
            <ChatButton
              chatClient={chatClient}
              buttonText={chatPrompts[3].buttonText}
              chatText={chatPrompts[3].chatText}
              shouldAutoSendChatText={shouldAutoSend}
            />
          </ButtonContainer>
          <ButtonContainer>
            <ChatButton
              chatClient={chatClient}
              buttonText="Help with something else"
            />
          </ButtonContainer>
        </FlexColumnContainer>
      </ExpertSetupContainer>
    </ExpertChatContainer>
  );
};
